import { useCallback, useMemo } from "react";

import {
  Box,
  Card,
  CardActions,
  CardHeader,
  Grid,
  Typography,
} from "@mui/material";

import { StyledRadio, Tooltip } from "@covertree/ui";
import { buildCardTestId } from "@covertree/utils";
import { PaymentPlanKey } from "../types";
import { PaymentTermCardTooltip } from "./PaymentTermCardTooltip";
import { useCheckout } from "../CheckoutProvider";

interface PaymentTermCardProps {
  disabled?: boolean;
  helpText?: string;
  onPaymentTermSelect: (key: PaymentPlanKey) => void;
  paymentPlanKey: PaymentPlanKey;
  selected: boolean;
  title: string;
  isAvailable: boolean;
  totalCost?: string;
}

export function PaymentTermCard({
  onPaymentTermSelect,
  selected,
  title,
  disabled,
  helpText,
  totalCost,
  paymentPlanKey,
  isAvailable,
}: PaymentTermCardProps) {
  const { selectedPaymentPlanKey, monthlyPaymentPlanStates } = useCheckout();
  const testId = useMemo(() => buildCardTestId(title), [title]);

  const handleCardClick = useCallback(() => {
    if (disabled) {
      return;
    }

    onPaymentTermSelect(paymentPlanKey);
  }, [disabled, paymentPlanKey]);

  return (
    <Card
      sx={[
        (theme) => ({
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          borderWidth: 1,
          "&:hover": {
            boxShadow: theme.shadows[2],
          },
          padding: {
            xs: 1,
            sm: 0,
          },
        }),
        ...(selected
          ? [
              {
                borderWidth: 2,
                borderColor: "primary.main",
              },
            ]
          : []),
        ...(disabled
          ? [
              {
                opacity: 0.6,
                cursor: "not-allowed",
                "&:hover": {
                  boxShadow: "none !important",
                },
              },
            ]
          : []),
      ]}
      aria-selected={selected}
      data-testid={`${testId}`}
      onClick={handleCardClick}
      role="option"
      aria-labelledby={`${testId}-label`}>
      <Box
        sx={{
          borderWidth: selected ? 0 : 1,
          borderColor: "transparent",
          borderStyle: "solid",
          display: "flex",
          px: {
            xs: 1,
            sm: 3,
          },
          flexGrow: 1,
          alignItems: "center",
        }}>
        <CardActions
          sx={(theme) => ({
            marginRight: 2,
            padding: 0,
            height: theme.spacing(8),
            pointerEvents: "none",
          })}>
          <StyledRadio
            data-testid={`${testId}-radio-button`}
            checked={selected}
            onClick={handleCardClick}
          />
        </CardActions>
        <Grid
          container
          display="flex"
          xs={12}
          alignItems={"center"}
          paddingTop={2}
          paddingBottom={2}>
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CardHeader
                title={title}
                titleTypographyProps={{
                  "data-testid": `${testId}-title`,
                  id: `${testId}-label`,
                }}
              />
              <Typography
                sx={{ ml: 1 }}
                color={selected ? "secondary" : "textSecondary"}>
                {helpText}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              justifyContent={"flex-end"}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}>
              <Typography>${totalCost}/year</Typography>
            </Box>
          </Grid>
          {(!isAvailable || selectedPaymentPlanKey === "mortgageBill") &&
            paymentPlanKey === "monthly" &&
            monthlyPaymentPlanStates && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip
                    dataTestId="monthly-payment-plan-tooltip"
                    tooltip={
                      <PaymentTermCardTooltip
                        monthlyPaymentPlanStates={monthlyPaymentPlanStates}
                      />
                    }
                    size="large"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: "none",
                        },
                      },
                    }}
                  />
                  <Typography
                    fontSize={(theme) => theme.spacing(2)}
                    fontStyle="italic">
                    Why isn&apos;t this available?
                  </Typography>
                </Box>
              </Grid>
            )}
        </Grid>
      </Box>
    </Card>
  );
}
